import { useMemo, useRef, useState, type FC } from 'react';
import { useEditor, useEditorState, EditorContent, type Editor, type JSONContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import {
    LuType, LuHeading1, LuHeading2, LuHeading3, LuBold, LuItalic, LuUnderline, LuStrikethrough,
    LuLink, LuList, LuListOrdered, LuQuote, LuMinus, LuAlignLeft, LuAlignCenter, LuAlignRight,
    LuAlignJustify, LuTable, LuBraces,
} from 'react-icons/lu';
import Select from '../Select';
import { Variavel, TabelaItens, TrechoACompletar } from './extensoesDocumento';
import FolhaDocumento, { MesaDaFolha, type EmpresaDaFolha } from './FolhaDocumento';
import { DOCUMENTO_VAZIO } from './conteudoModelo';
import type { TipoDocumento, VariavelDocumento } from '../../services/ModeloDocumentoService';
import '../NoteEditor.css';
import './ModeloDocumentoEditor.css';

interface Props {
    /** JSON do Tiptap já desserializado. Null começa com um parágrafo vazio. */
    conteudoInicial: JSONContent | null;
    variaveis: VariavelDocumento[];
    onChange?: (json: JSONContent) => void;
    /* O que a folha em volta do texto mostra — o mesmo da prévia. */
    tipo: TipoDocumento;
    validadeDias: number | null;
    empresa: EmpresaDaFolha;
}

/**
 * O editor do modelo de proposta/orçamento/contrato. A barra é a das anotações
 * (`ne-`); o texto é escrito DENTRO da folha da prévia (FolhaDocumento), com
 * a tipografia do PDF, entre o cabeçalho e o rodapé que o gerador imprime.
 * Ver e editar mostram o mesmo papel (06/10/2026). Salva JSON (é o que o PDF
 * percorre), não tem imagem nem código (o gerador não desenha) e tem os dois
 * blocos próprios: a variável (chip com o nome) e a tabela de itens.
 */
const ModeloDocumentoEditor: FC<Props> = ({ conteudoInicial, variaveis, onChange, tipo, validadeDias, empresa }) => {
    /* Os nomes entram por ref: o nó lê o nome na hora de desenhar e a lista
       pode chegar depois do editor existir. A ref é preenchida DURANTE o
       render, não num efeito: o `useEditor` cria o editor (e desenha os chips)
       antes de qualquer efeito rodar, e com o efeito todo chip nascia com a
       chave ("empresa.nome") em vez do nome. */
    const nomesRef = useRef<Record<string, string>>({});
    nomesRef.current = useMemo(() => Object.fromEntries(variaveis.map(v => [v.chave, v.nome])), [variaveis]);
    const exemplos = useMemo(() => Object.fromEntries(variaveis.map(v => [v.chave, v.exemplo])), [variaveis]);
    /* O título da folha muda com o texto: com variável de organização, sai o nome dela. */
    const [atual, setAtual] = useState<JSONContent>(conteudoInicial ?? DOCUMENTO_VAZIO);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                codeBlock: false,
                code: false,
                link: { openOnClick: false },
            }),
            Placeholder.configure({
                placeholder: ({ node }) => node.type.name === 'paragraph' ? 'Escreva o texto do documento…' : '',
            }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Variavel.configure({ obterNome: chave => nomesRef.current[chave] ?? chave }),
            TabelaItens,
            TrechoACompletar,
        ],
        content: conteudoInicial ?? DOCUMENTO_VAZIO,
        onUpdate: ({ editor: e }) => {
            const json = e.getJSON();
            setAtual(json);
            onChange?.(json);
        },
        editorProps: { attributes: { class: 'md-doc', 'aria-label': 'Texto do modelo' } },
    });

    if (!editor) return <div className="ne-carregando" />;

    return (
        <div className="md-editor">
            <Barra editor={editor} variaveis={variaveis} />
            <MesaDaFolha editavel>
                <FolhaDocumento molde conteudo={atual} tipo={tipo} validadeDias={validadeDias}
                    empresa={empresa} exemplos={exemplos}>
                    <EditorContent editor={editor} />
                </FolhaDocumento>
            </MesaDaFolha>
        </div>
    );
};

const Barra: FC<{ editor: Editor; variaveis: VariavelDocumento[] }> = ({ editor, variaveis }) => {
    const ativo = useEditorState({
        editor,
        selector: ({ editor: e }) => ({
            paragrafo: e.isActive('paragraph'),
            h1: e.isActive('heading', { level: 1 }),
            h2: e.isActive('heading', { level: 2 }),
            h3: e.isActive('heading', { level: 3 }),
            negrito: e.isActive('bold'),
            italico: e.isActive('italic'),
            sublinhado: e.isActive('underline'),
            riscado: e.isActive('strike'),
            link: e.isActive('link'),
            marcador: e.isActive('bulletList'),
            numerada: e.isActive('orderedList'),
            citacao: e.isActive('blockquote'),
            esquerda: e.isActive({ textAlign: 'left' }),
            centro: e.isActive({ textAlign: 'center' }),
            direita: e.isActive({ textAlign: 'right' }),
            justificado: e.isActive({ textAlign: 'justify' }),
        }),
    });

    const opcoesVariaveis = useMemo(() => variaveis.map(v => ({
        value: v.chave, label: v.nome, meta: `Ex.: ${v.exemplo}`, group: v.grupo,
    })), [variaveis]);

    const definirLink = () => {
        const atual = editor.getAttributes('link').href as string | undefined;
        const url = window.prompt('Endereço do link', atual ?? 'https://');
        if (url === null) return;
        if (url.trim() === '') { editor.chain().focus().unsetLink().run(); return; }
        editor.chain().focus().setLink({ href: url.trim() }).run();
    };

    const botao = (chave: string, rotulo: string, icone: React.ReactNode, aoClicar: () => void, ligado = false) => (
        <button key={chave} type="button" className={`ne-bar-b ${ligado ? 'is-on' : ''}`}
            title={rotulo} aria-label={rotulo} aria-pressed={ligado}
            onMouseDown={e => e.preventDefault()} onClick={aoClicar}>
            {icone}
        </button>
    );

    return (
        <div className="ne-bar md-bar" role="toolbar" aria-label="Formatação do modelo">
            <div className="ne-bar-col md-bar-col">
                {botao('p', 'Texto', <LuType size={15} />, () => editor.chain().focus().setParagraph().run(), ativo.paragrafo)}
                {botao('h1', 'Título 1', <LuHeading1 size={15} />, () => editor.chain().focus().toggleHeading({ level: 1 }).run(), ativo.h1)}
                {botao('h2', 'Título 2', <LuHeading2 size={15} />, () => editor.chain().focus().toggleHeading({ level: 2 }).run(), ativo.h2)}
                {botao('h3', 'Título 3', <LuHeading3 size={15} />, () => editor.chain().focus().toggleHeading({ level: 3 }).run(), ativo.h3)}

                <span className="ne-bar-sep" />

                {botao('b', 'Negrito', <LuBold size={15} />, () => editor.chain().focus().toggleBold().run(), ativo.negrito)}
                {botao('i', 'Itálico', <LuItalic size={15} />, () => editor.chain().focus().toggleItalic().run(), ativo.italico)}
                {botao('u', 'Sublinhado', <LuUnderline size={15} />, () => editor.chain().focus().toggleUnderline().run(), ativo.sublinhado)}
                {botao('s', 'Riscado', <LuStrikethrough size={15} />, () => editor.chain().focus().toggleStrike().run(), ativo.riscado)}
                {botao('link', 'Link', <LuLink size={15} />, definirLink, ativo.link)}

                <span className="ne-bar-sep" />

                {botao('al', 'Alinhar à esquerda', <LuAlignLeft size={15} />, () => editor.chain().focus().setTextAlign('left').run(), ativo.esquerda)}
                {botao('ac', 'Centralizar', <LuAlignCenter size={15} />, () => editor.chain().focus().setTextAlign('center').run(), ativo.centro)}
                {botao('ar', 'Alinhar à direita', <LuAlignRight size={15} />, () => editor.chain().focus().setTextAlign('right').run(), ativo.direita)}
                {botao('aj', 'Justificar', <LuAlignJustify size={15} />, () => editor.chain().focus().setTextAlign('justify').run(), ativo.justificado)}

                <span className="ne-bar-sep" />

                {botao('ul', 'Lista com marcador', <LuList size={15} />, () => editor.chain().focus().toggleBulletList().run(), ativo.marcador)}
                {botao('ol', 'Lista numerada', <LuListOrdered size={15} />, () => editor.chain().focus().toggleOrderedList().run(), ativo.numerada)}
                {botao('quote', 'Citação', <LuQuote size={15} />, () => editor.chain().focus().toggleBlockquote().run(), ativo.citacao)}
                {botao('hr', 'Divisória', <LuMinus size={15} />, () => editor.chain().focus().setHorizontalRule().run())}

                <span className="ne-bar-sep" />

                <div className="md-var-pick" onMouseDown={e => e.preventDefault()}>
                    <Select
                        size="control"
                        block={false}
                        value=""
                        placeholder="Inserir variável"
                        icon={<LuBraces size={14} />}
                        options={opcoesVariaveis}
                        searchFrom={8}
                        portal
                        ariaLabel="Inserir variável"
                        emptyText="Nenhuma variável"
                        onChange={chave => { if (chave) editor.chain().inserirVariavel(chave).run(); }}
                    />
                </div>
                {botao('tabela', 'Tabela de itens', <LuTable size={15} />, () => editor.chain().inserirTabelaItens().run())}
            </div>
        </div>
    );
};

export default ModeloDocumentoEditor;
